import {
  AfterViewInit,
  Component,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MODAL } from 'src/app/services/modal.service';
import { ModalService } from '../../services/modal.service';
import { CurrentService } from 'src/app/services/current.service';
import { CustomerService } from '../../services/customer.service';
import { ActivatedRoute, Router } from '@angular/router';
import domtoimage from 'dom-to-image';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { BaseAppService } from 'src/app/services/configurators-2d/base-app-service';
import { Configuration2D } from 'src/app/models/configurators-2d/configuration2d';
import { BaseConfigurator } from './configurator/base-configurator';
import {
  CARPENTRY_CONFIGURATOR,
  CLOSET_DOORS_CUSTOM_CONFIGURATOR,
  DECKING_BOARDS_CONFIGURATOR,
  FENCE_CONFIGURATOR,
  WORKBENCH_CONFIGURATOR,
} from 'src/app/providers/configurators-2d.providers';
import { Project } from 'src/app/models/project';
import { ProcessQuoteService } from 'src/app/services/process-quote.service';
import { getAppUrl, getContext } from 'src/app/services/helpers/app-context-helper';
import { IPreview } from './configurator/previews/i-preview';
import { ClosetDoorsStandardService } from 'src/app/services/configurators-2d/applications/closet-doors-standard.service';
import { ClosetDoorsStandardComponent } from './closet-doors/standard/closet-doors-standard.component';
import { environment } from '../../../environments/environment';
import { AlertOptions, AlertService } from 'src/app/_alert';
import { TranslateService } from '@ngx-translate/core';
import { ConfiguratorUpdateStructureStep } from './configurator/configurator-update-structure-step';
import { ConfiguratorStructureStep } from './configurator/configurator-structure-step';
import { CommonOrder } from 'src/app/models/configurators-2d/common-order';
import { GTM } from 'src/app/services/gtm.service';
import { WorkbenchService } from 'src/app/services/configurators-2d/applications/workbench.service';
import { MaterialTypeEnum } from 'src/app/enum/workbench/material-type-enum';
import { PrintService } from 'src/app/services/print.service';
import { TunnelNavigationStore } from '../../stores/tunnel/tunnel-navigation.store';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import { KitchenService } from '../../services/configurators-2d/applications/kitchen.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { KingfisherBasketService } from 'src/app/services/kingfisher/kingfisher-basket.service';
import { AddToBasketResult } from 'src/app/models/kingfisher-basket/add-to-basket-result';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';

const ERROR_TO_IGNORE = 'ignore';

@Component({
  selector: 'ng-configurators-2d',
  templateUrl: './configurators-2d.component.html',
})
@Injectable({ providedIn: 'root' })
export class Configurators2DComponent implements OnInit, AfterViewInit, OnDestroy {
  isVendorApp: boolean;

  /** Enum des contextes de configurations */
  readonly CONFIG_CONTEXT = CONFIG_CONTEXT;

  /** Etape de la configuration */
  steps: any[];

  /** Chargement global de la page */
  isGlobalLoading: boolean = true;

  /** Chargement du bouton Enregistrer */
  isSaveLoading: boolean = false;

  /** Etape actuelle de la configuration */
  step: number = 1;

  /** Etape finale de la configuration */
  finalStep: number;

  /** Chargement du bouton suivant */
  isNextButtonLoading: boolean = false;

  /** Chargement des boutons de devis et commande */
  isQuoteAndOrderLoading = false;

  /** Ajout au panier Castorama en cours */
  isAddingToBasket = false;

  /** Envoi à COM+ en cours */
  orderingInFlight = false;

  /** URL de base des images */
  baseImgURL: string = undefined;

  /** Observable sur le resize de la fenetre */
  resizeObservable$: Observable<Event>;

  /** Souscription sur le resize de la fenetre */
  resizeSubscription$: Subscription;

  /** Le titre est en train d'être modifier */
  isTitleEditing: boolean = false;

  /** Sous étape de la configuration */
  subStep: number;

  /** Liste des configurateurs (applications) */
  configurators = new Map<string, BaseConfigurator<Configuration2D, BaseAppService<Configuration2D>>>();

  finishesRendered: boolean = false;

  /** Configurateur interne */
  internalConfig: boolean = true;

  /** Configurateur avec preview */
  configWithPreview: boolean = true;

  /** Configurateurs dont il faut sauvegarder le PDF du devis */
  savePdf: boolean = false;

  /** Redirection vers la dernière étape valide en attente de la fin du chargement du projet */
  private pendingLastValidStepRedirect: boolean = false;

  /** Boutons quote et order désactivés */
  areQuoteAndOrderDisabled: boolean = false;

  /** Alert options */
  alertOptions: AlertOptions = {
    autoClose: true,
  };

  /** Crédence seule */
  isOnlySplashback: boolean = false;

  isSplashbackForceDisabled = false;

  /**La liste des abonnements, pour les unsubscribe au ngDestroy*/
  private readonly subscriptions: Subscription[] = [];

  /**Le context principal dans lequel le configurateur est appelé. Diffère de this.customer.context quand on est dans un tunnel d'un autre configurateur.
   * eg : Tunnel Kitchen qui possède une étape de Workbench
   * */
  private mainContext: CONFIG_CONTEXT = undefined;

  /** Le configurateur a un prix */
  get hasPrice(): boolean {
    if (
      this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD ||
      this.customer.context === CONFIG_CONTEXT.CARPENTRY
    ) {
      return true;
    }

    if (this.appService?.configuration?.resume) {
      return this.appService.configuration.resume?.priceErrorCode == null;
    }

    return false;
  }

  /**
   * Affichage du CTA « Ajouter au panier » : côté client uniquement, quand un prix est disponible,
   * et si l'envoi au panier est activé pour ce configurateur dans l'administration des fonctionnalités.
   */
  get showAddToBasketButton(): boolean {
    return !this.isVendorApp && this.hasPrice && this.featureSettingsService.isBasketSendEnabled(this.customer.context);
  }

  /** Le projet courant a-t-il déjà été ajouté au panier durant la session ? */
  get projectAddedToBasket(): boolean {
    return this.kingfisherBasketService.isAddedToBasket(this.currentBasketConfigurationId);
  }

  /** Identifiant de la configuration courante, selon le configurateur (le standard utilise un id dédié). */
  private get currentBasketConfigurationId(): string {
    return this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
      ? this.current.closetDoors?.id
      : this.appService?.configuration?.id;
  }

  /** Titre du projet courant, selon le configurateur (le standard utilise une config dédiée). */
  private get currentProjectTitle(): string {
    return this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
      ? this.current.closetDoors?.title
      : this.appService?.configuration?.title;
  }

  /** Type de configurateur courant, pour le tracking GTM (aligné sur le libellé d'accueil). */
  private get currentProjectType(): string {
    // Le standard passe par sa clé de contexte (appService.distribId n'est pas fiable dans ce cas).
    const configurator = this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
      ? CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
      : this.appService?.distribId;
    return this.translate.instant('homepage.' + configurator + '.title');
  }

  @ViewChildren('preview') previews!: QueryList<IPreview>;
  preview: IPreview;

  /** Récupère l'appComponent spécifique à chaque configurateur */
  get appComponent(): BaseConfigurator<Configuration2D, BaseAppService<Configuration2D>> {
    if (this.configurators.has(this.customer.context)) {
      return this.configurators.get(this.customer.context);
    }

    let type: InjectionToken<BaseConfigurator<Configuration2D, BaseAppService<Configuration2D>>>;
    switch (this.customer.context) {
      case CONFIG_CONTEXT.CLOSET_DOORS_CUSTOM:
        type = CLOSET_DOORS_CUSTOM_CONFIGURATOR;
        break;
      case CONFIG_CONTEXT.FENCE:
        type = FENCE_CONFIGURATOR;
        break;
      case CONFIG_CONTEXT.DECKING_BOARDS:
        type = DECKING_BOARDS_CONFIGURATOR;
        break;
      case CONFIG_CONTEXT.WORKBENCH:
        type = WORKBENCH_CONFIGURATOR;
        break;
      case CONFIG_CONTEXT.CARPENTRY:
        type = CARPENTRY_CONFIGURATOR;
        break;
      default:
        return undefined;
    }

    let configurator = this.injector.get(type);
    this.configurators.set(this.customer.context, configurator);
    return configurator;
  }

  /** Récupère l'appService spécifique à chaque configurateur */
  get appService(): BaseAppService<Configuration2D> {
    return this.appComponent?.service;
  }

  get isTunnel(): boolean {
    return this.tunnelNavigationStore?.isTunnelValue;
  }

  constructor(
    private readonly modalService: ModalService,
    public closetDoorsService: ClosetDoorsStandardService,
    public workbenchService: WorkbenchService,
    public current: CurrentService,
    public customer: CustomerService,
    private readonly print: PrintService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translate: TranslateService,
    public cdc: ClosetDoorsStandardComponent,
    private readonly injector: Injector,
    private readonly processQuoteService: ProcessQuoteService,
    public alertService: AlertService,
    public gtm: GTM,
    private readonly tunnelNavigationStore: TunnelNavigationStore,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly kitchenService: KitchenService,
    private readonly kingfisherBasketService: KingfisherBasketService,
    private readonly featureSettingsService: FeatureSettingsService
  ) {
    this.customer.context = getContext(this.router.url) ?? this.customer.context;
    if (
      this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_CUSTOM ||
      this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
    ) {
      if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
        this.baseImgURL = this.closetDoorsService.apiUrl;
      }
      this.internalConfig = environment.configurators2d.applications[CONFIG_CONTEXT.CLOSET_DOORS].internalConfig;
      this.configWithPreview = environment.configurators2d.applications[CONFIG_CONTEXT.CLOSET_DOORS].configWithPreview;
      this.savePdf = environment.configurators2d.applications[CONFIG_CONTEXT.CLOSET_DOORS].savePdf;
    } else if (this.customer.context) {
      this.baseImgURL = this.appService.apiUrl;
      this.internalConfig = environment.configurators2d.applications[this.customer.context].internalConfig;
      this.configWithPreview = environment.configurators2d.applications[this.customer.context].configWithPreview;
      this.savePdf = environment.configurators2d.applications[this.customer.context].savePdf;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(subscription => {
      subscription.unsubscribe();
    });
  }

  ngOnInit(): void {
    this.setGlobalLoading(true);
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.subscribe();
    this.route.params.subscribe(params => {
      this.step = 1;
      this.pendingLastValidStepRedirect = params.projectId !== undefined;
    });
    this.isVendorApp = environment.vendor;
    // Précharge les réglages de fonctionnalités pour piloter l'affichage du CTA « Ajouter au panier ».
    void this.featureSettingsService.getSettings();
    this.subscribeToObservables();
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      this.baseImgURL = this.closetDoorsService.apiUrl;
      this.customer.context = CONFIG_CONTEXT.CLOSET_DOORS_STANDARD;
      this.finalStep = 5;
      this.initStepper();
    } else {
      this.appService?.reset();
      this.finalStep = this.appComponent.steps.length;
    }
  }

  ngAfterViewInit() {
    this.preview = this.getPreview();
    // Tracking de l'initialisation d'un configurateur 2D (Step 1)
    this.trackPageWithGtm();
  }

  private subscribeToObservables() {
    this.subscriptions.push(
      this.tunnelDataStore.getMainContext().subscribe(mainContext => {
        this.mainContext = mainContext;
      })
    );
  }

  /**
   * Permet de récupérer la preview utilisée dans le configurateur
   */
  private getPreview(): IPreview {
    if (this.previews.length === 0) {
      return null;
    }

    return this.previews.first;
  }

  /**
   * Fonction de trigger du chargement global
   * @param isGlobalLoading chargement global
   */
  setGlobalLoading(isGlobalLoading: boolean): void {
    if (!isGlobalLoading) {
      this.goToLastValidStep();
    }

    this.isGlobalLoading = isGlobalLoading;
  }

  /**
   * Positionne l'utilisateur sur la dernière étape accessible du projet rouvert.
   */
  private goToLastValidStep(): void {
    if (!this.pendingLastValidStepRedirect) {
      return;
    }

    this.pendingLastValidStepRedirect = false;

    if (this.isTunnel || !this.hasSavedProject()) {
      return;
    }

    let lastValidStep = 1;
    for (let step = 2; step <= this.finalStep; step++) {
      if (!this.isStepReachable(step)) {
        break;
      }

      lastValidStep = step;
    }

    if (lastValidStep === this.step) {
      return;
    }

    this.step = lastValidStep;
    this.trackPageWithGtm();
  }

  /**
   * Indique si la configuration en cours correspond à un projet déjà enregistré.
   */
  private hasSavedProject(): boolean {
    return this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
      ? this.current.closetDoors?.id != null
      : this.appService?.configuration?.id != null;
  }

  /**
   * Indique si l'étape est accessible, une erreur de garde valant étape inaccessible.
   */
  private isStepReachable(step: number): boolean {
    try {
      return this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
        ? this.cdc.checkPageChangeCapability(step)
        : this.appComponent.checkPageChangeCapability(step);
    } catch (error) {
      logError(error);
      return false;
    }
  }

  setQuoteAndOrderDisabled(areQuoteAndOrderDisabled: boolean): void {
    this.areQuoteAndOrderDisabled = areQuoteAndOrderDisabled;
  }

  onFinishesRendered() {
    this.finishesRendered = true;
  }

  /**
   * Fonction de rendu de l'image des portes de placard pour l'enregistrement
   * @param resize
   * @param width
   * @param height
   */
  renderToImgClosetDoors(resize: boolean = true, width: number = 300, height: number = 225) {
    return new Promise<string>(resolve => {
      let node = document.getElementById('renderDiv');
      let divs = node.getElementsByTagName('div');
      for (let i = 0; i < divs.length; i++) {
        divs.item(i).className = divs.item(i).className.replace('active', '');
        if (divs.item(i).className.includes('decoration-button')) {
          divs.item(i).remove();
        }
      }

      document.getElementById('renderDiv').style.display = 'flex';
      document.getElementById('rendering').style.display = 'none';

      this.renderToImg(resize, width, height, 'renderDiv')
        .then(img => resolve(img))
        .finally(() => {
          document.getElementById('rendering').style.display = 'flex';
          document.getElementById('renderDiv').style.display = 'none';
        });
    });
  }

  /**
   * Fonction de rendu de l'image de la preview pour l'enregistrement
   * @param resize
   * @param width
   * @param height
   * @param div
   */
  renderToImg(resize: boolean = true, width: number = 300, height: number = 225, div: string = 'rendering') {
    // Pour le workbench, on utilise htmlToImage via le composant preview
    // pour capturer fidèlement le canvas WebGL + overlays 2D (indicateurs, boutons, plan indexes)
    if (this.customer.context === CONFIG_CONTEXT.WORKBENCH) {
      return new Promise<string>(async (resolve) => {
        const previewComponent = this.previews?.first as any;
        if (previewComponent && typeof previewComponent.captureAsBase64 === 'function') {
          const dataUrl = await previewComponent.captureAsBase64();
          if (dataUrl && resize) {
            const resized = await this.resizeImage(dataUrl, width, height);
            resolve(resized);
          } else {
            resolve(dataUrl ?? '');
          }
        } else {
          // Fallback domtoimage si le composant n'est pas disponible
          domtoimage
            .toPng(document.getElementById(div))
            .then(async (dataUrl) => {
              resolve(resize ? await this.resizeImage(dataUrl, width, height) : dataUrl);
            })
            .catch((error) => { logError("Impossible de capturer l'image", error); resolve(''); });
        }
      });
    }

    return new Promise<string>(resolve => {
      domtoimage
        .toPng(document.getElementById(div))
        .then(async (dataUrl) => {
          resolve(resize ? await this.resizeImage(dataUrl, width, height) : dataUrl);
        })
        .catch((error) => {
          logError("Impossible de capturer l'image", error);
          resolve('');
        });
    });
  }

  /**
   * Fonction de réduction des images en base 64
   * @param uri
   * @param width
   * @param height
   * @returns string base 64
   */
  resizeImage(uri, width, height): Promise<string> {
    return new Promise(function (resolve) {
      let img = document.createElement('img');
      img.onload = function () {
        let canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        let dataUri = canvas.toDataURL().toString();
        resolve(dataUri);
      };
      img.crossOrigin = '';
      img.src = uri;
    });
  }

  /**
   * Récupère une image et la convertie en base 64 depuis une url
   * @param imageUrl
   * @returns
   */
  async getBase64ImageFromUrl(imageUrl) {
    let res = await fetch(imageUrl);
    let blob = await res.blob();

    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.addEventListener(
        'load',
        function () {
          resolve(reader.result);
        },
        false
      );

      reader.onerror = () => {
        return reject(this);
      };
      reader.readAsDataURL(blob);
      reader.addEventListener('load', () => resolve(reader.result), false);
      reader.onerror = () => reject(this);
    });
  }

  /**
   * Fonction de changement de page par bouton suivant
   * @param up: boolean (page suivante si true, précédente si false)
   */
  changePage(up: boolean = true): any {
    this.isNextButtonLoading = true;
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      if (this.cdc.checkPageChangeCapability(this.step + 1)) {
        this.cdc.initPage(this.step + 1).then(
          () => {
            this.step = this.step + 1;
            //Scroll en haut de page sur mobile au changement de page
            if (document.documentElement.clientWidth < 767) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            this.isNextButtonLoading = false;
            // Tracking des VPV
            this.trackPageWithGtm();
          },
          err => {
            this.isNextButtonLoading = false;
          }
        );
      } else {
        this.isNextButtonLoading = false;
      }
    } else {
      let component = this.appComponent;
      let nextStep: number = up ? this.step + 1 : this.step - 1;
      if (component.checkPageChangeCapability(nextStep)) {
        this.step = nextStep;
        //Scroll en haut de page sur mobile au changement de page
        if (document.documentElement.clientWidth < 767) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        this.isNextButtonLoading = false;
      }
      // Tracking des VPV
      this.trackPageWithGtm();
    }
  }

  isNextButtonActive(): boolean {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return this.cdc.isNextButtonActive(this.step);
    }

    return this.appComponent.isNextButtonActive(this.step);
  }

  isNextButtonHidden(): boolean {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return this.cdc.isNextButtonHidden(this.step);
    }

    return this.appComponent.isNextButtonHidden(this.step, this.finalStep);
  }

  /**
   * Fonction de récupération de la sous-étape de la configuration
   * @param event number
   */
  getSubstep(event: number) {
    this.subStep = event;
  }

  /**
   * Fonction de navigation entre les pages depuis le stepper
   * @param step etape à accéder
   */
  navigatePage(step: number): any {
    this.isNextButtonLoading = true;
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      if (this.cdc.checkPageChangeCapability(step)) {
        this.cdc.initPage(step).then(
          () => {
            this.step = step;
            // Tracking des VPV
            this.trackPageWithGtm();
            this.isNextButtonLoading = false;
          },
          err => {
            this.isNextButtonLoading = false;
          }
        );
      } else {
        this.isNextButtonLoading = false;
      }
    } else {
      if (this.appComponent.checkPageChangeCapability(step)) {
        this.step = step;
      }

      // Tracking des VPV
      this.trackPageWithGtm();
      this.isNextButtonLoading = false;
    }
  }

  /**
   * Réinitialisation du composant pour la création d'un nouveau projet
   */
  newProject(): any {
    // appli vendeur
    if (this.isVendorApp) {
      if (this.customer.userDatas !== null) {
        this.modalService.display(MODAL.KEEP_CUSTOMER, {
          onClose: () => {
            this.modalService.close();
            this.step = 1;
            this.resetProject();
          },
        });
      } else {
        this.step = 1;
        this.resetProject();
      }
    }
    // appli client
    else {
      this.step = 1;
      this.resetProject();
    }
  }

  private resetProject() {
    let distribId: string;

    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      this.current.reset();
      distribId = this.closetDoorsService.distribId;
    } else {
      this.appService.reset();
      distribId = this.appService.distribId;
    }

    const routeUrl = getAppUrl(distribId);
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(async () => {
      await this.router.navigate(['/' + routeUrl + '/configuration']);
    });
  }

  /**
   * Ouverture de la modale de sélection des projets
   */
  myProjects(): any {
    this.modalService.display(MODAL.PROJECTS);
  }

  /**
   * Sauvegarde du projet dans l'état
   */
  async saveProject(): Promise<any> {
    if (this.isSaveLoading) return;
    this.isSaveLoading = true;

    try {
      await this.selectCustomerIfNeeded(async () => {
        try {
          if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
            await this.closetDoorsService.saveProject(this.current.closetDoors);
            const imgUrl = await this.renderToImgClosetDoors(true);
            await this.closetDoorsService.saveVisual(this.current.closetDoors.id, { data: imgUrl });
            // Evenement d'enregistrement du projet
            this.trackEventWithGtm('enregistrer-projet');
            this.alertService.success(this.translate.instant('generic.saved-success'), this.alertOptions);
          } else {
            if (this.configWithPreview) {
              const imgUrl = await this.renderToImg();
              this.appService.configuration.thumbnail = { data: imgUrl };
            }
            await this.appService.saveProject();
            // Evenement d'enregistrement du projet
            this.trackEventWithGtm('enregistrer-projet');
            this.alertService.success(this.translate.instant('generic.saved-success'), this.alertOptions);
          }
        } catch (err) {
          logError(err);
          this.alertService.error(this.translate.instant('generic.saved-failed'), this.alertOptions);
        } finally {
          this.isSaveLoading = false
        }
      });
    } catch (err) {
      if (err === ERROR_TO_IGNORE) return;
      logError(err);
      this.alertService.error(this.translate.instant('generic.saved-failed'), this.alertOptions);
    } finally {
      this.isSaveLoading = false
    }
  }

  /**
   * Fonction de duplication de la configuration
   */
  async duplicateProject(): Promise<void> {
    await this.selectCustomerIfNeeded(async () => {
      let appDistribId: string;
      let configurationId: string;
      let configurationTitle: string;

      if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
        await this.closetDoorsService.assignCustomer(this.current.closetDoors.id);
        appDistribId = this.closetDoorsService.distribId;
        configurationId = this.current.closetDoors.id;
        configurationTitle = this.current.closetDoors.title;
      } else {
        let apiService = this.appService;
        appDistribId = apiService.distribId;
        configurationId = apiService.configuration.id;
        configurationTitle = apiService.configuration.title;
      }

      this.modalService.display(MODAL.PROJECT_DUPLICATE, {
        project: {
          projectID: configurationId,
          distributionID: appDistribId,
          projectName: configurationTitle,
        },
        onClose: async (duplicateProject: Project) => {
          if (duplicateProject) {
            const distribId = this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
              ? this.closetDoorsService.distribId
              : this.appService.distribId;
            await this.router.navigateByUrl('/', { skipLocationChange: true });
            await this.router.navigate([getAppUrl(distribId), 'open', duplicateProject.projectUUID]);
          }
          this.modalService.close();
        },
      });
    });
  }

  /**
   * Fonction d'activation/désactivation du bouton mes projets
   * @returns boolean
   */
  isMyProjectsButtonDisabled() {
    return !this.customer.userDatas;
  }

  /**
   * Fonction d'activation/désactivation du bouton de duplication des projets
   * @returns boolean
   */
  isDuplicateProjectButtonDisabled() {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return !this.current.closetDoors.id || !this.customer.userDatas;
    }

    return !this.appService.configuration.id || this.appService.configuration.hasDisabledProducts;
  }

  /**
   * Fonction d'activation/désactivation du bouton de sauvegarde du projet
   * @returns boolean
   */
  isSaveProjectButtonDisabled(): boolean {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return false;
    }

    return this.appComponent.isSaveProjectButtonDisabled();
  }

  /**
   * Fonction d'activation/désactivation des boutons de devis et commande
   * @returns boolean
   */
  isOrderButtonDisabled(): boolean {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return false;
    }
    return this.appComponent.isOrderButtonDisabled(this.step);
  }

  hasNationalDiscount() {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_CUSTOM) {
      if (!this.hasManualDiscount() && this.appService.configuration.resume?.totalDiscountHT != null) {
        return true;
      }
    }
    return false;
  }

  isContextWorkbench() {
    if (this.customer.context === CONFIG_CONTEXT.WORKBENCH) {
      // Vérifie s'il n'y a aucun élément dans workbenchConfigurationPlans avec material.materialType égal à MaterialTypeEnum.Quartz

      return !this.workbenchService.configuration.workbenchConfigurationPlans.some(
        plan => plan.material.materialType == MaterialTypeEnum.Quartz
      );
    }
    return true;
  }

  hasManualDiscount() {
    return this.appService.hasManualDiscount ?? false;
  }

  updateDiscount(event: Event) {
    event.stopPropagation();
    let hasDiscount = this.hasManualDiscount();
    this.modalService.display(MODAL.DISCOUNT_MANUAL, {
      hasDiscount,
      appService: this.appService,
      onClose: () => {
        this.modalService.close();
      },
    });
  }

  /**
   * Fonction d'ouverture du devis
   */
  async openQuotation(): Promise<void> {
    if (this.isQuoteAndOrderLoading) return;
    this.isQuoteAndOrderLoading = true;

    try {
      await this.selectCustomerIfNeeded(async () => await this.openQuotationByContext());
    } catch (e) {
      if (e === ERROR_TO_IGNORE) return;
      logError(e);
      alert('Une erreur est survenue, le devis ne peut pas être généré.');
    } finally {
      this.isQuoteAndOrderLoading = false;
    }
  }

  /**
   * Ajoute l'intégralité de la liste d'achats au panier e-commerce Castorama.
   * Affiche un loader pendant le traitement, met à jour le CTA en cas de succès
   * et affiche un message générique en cas d'erreur.
   */
  async addToBasket(): Promise<void> {
    if (this.isAddingToBasket || this.projectAddedToBasket) {
      return;
    }

    this.trackEventWithGtm('clic-ajout-panier');

    try {
      // Un panier est rattaché à un client : on s'assure d'abord qu'il est identifié.
      await this.selectCustomerIfNeeded(async () => await this.sendProjectToBasket());
    } catch (err) {
      if (err === ERROR_TO_IGNORE) {
        return;
      }
      logError(err);
    }
  }

  /**
   * Envoie la configuration courante au panier une fois le client identifié.
   * Le back dérive lui-même les produits (SKU + quantités) à partir du devis.
   */
  private async sendProjectToBasket(): Promise<void> {
    this.isAddingToBasket = true;

    try {
      const result = await this.addCurrentProjectToBasket();
      this.trackEventWithGtm('ajout-panier-succes', { productCount: result.requestedCount });
      this.modalService.display(MODAL.BASKET_CONFIRMATION, {
        projectName: this.currentProjectTitle,
        totalAmount: result.totalAmount,
        projectType: this.currentProjectType,
        projectId: this.currentBasketConfigurationId,
      });
    } catch (err) {
      logError(err);
      this.trackEventWithGtm('ajout-panier-echec');
      this.alertService.error(this.translate.instant('2d-configurator.buttons.basket-error'), this.alertOptions);
    } finally {
      this.isAddingToBasket = false;
    }
  }

  /**
   * Ajoute le projet courant au panier. Le back dérive les produits du devis ; on gère ici le cas
   * particulier des portes de placard standard (service et identifiant de configuration dédiés).
   */
  private addCurrentProjectToBasket(): Promise<AddToBasketResult> {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      const configurationId = this.current.closetDoors.id;
      return this.closetDoorsService
        .setQuotation(configurationId)
        .then(() => this.kingfisherBasketService.addProjectToBasket(this.closetDoorsService.apiUrl, configurationId));
    }

    return this.appService
      .saveProject()
      .then(() => this.kingfisherBasketService.addProjectToBasket(this.appService.apiUrl, this.appService.configuration.id));
  }

  async openQuotationByContext(): Promise<void> {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      await this.closetDoorsService.setQuotation(this.current.closetDoors.id);
      let visual = await this.renderToImgClosetDoors();
      await this.closetDoorsService.saveVisual(this.current.closetDoors.id, {
        data: visual,
      });
      let project = await this.closetDoorsService.getQuotation(this.current.closetDoors.id);
      this.trackEventWithGtm('ouverture-devis');
      this.modalService.display(MODAL.CLOSET_DOORS_QUOTE, {
        project: project,
        thumb: await this.renderToImgClosetDoors(false),
      });
    } else {
      //if (await this.appComponent.checkInstallation()) {
      if (this.configWithPreview) {
        this.appService.configuration.thumbnail = {
          data: await this.renderToImg(),
        };
      }
      await this.appService.saveProject();
      // Actuellement, chaque clic sur le bouton "Générer un devis" génère un nouveau devis
      // Il n'est pas possible de récupérer un devis valide existant
      let project = await this.appService.getQuotation();

      //Si crédence seule ne pas prendre en compte plan et forfait
      if (this.isOnlySplashback) {
        project.categories = project.categories.filter(p => {
          const name = p.nameFr?.trim().toLowerCase();
          return !name.startsWith('plan');
        });
      }

      if (project.categories.some(c => c.products.some(p => p.vat))) {
        //S'il s'agit d'un devis avec plusieurs Tva
        this.trackEventWithGtm('ouverture-devis');
        this.modalService.display(MODAL.MULTI_VAT_QUOTE, {
          project: project,
          context: this.customer.context,
          thumb: this.configWithPreview ? await this.renderToImg(false, 3000, 2250) : undefined,
        });
      } else {
        this.trackEventWithGtm('ouverture-devis');

        // Correspond à 20% de TVA
        project.percentTVA = project.percentTVA != 0 && project.percentTVA != undefined ? project.percentTVA : 0.2;
        let imgPdfBase64 = await this.appService.generateNewPdf({
          configContext: this.customer.context,
          configurationId2D: this.appService.configuration.id,
          customerContext: this.translate.instant('popins.products_list.print_title_' + this.customer.context),
          configThumbnail: await this.renderToImg(true, 3000, 2250),
          disclaimer: this.getDisclaimersContext(this.customer.context),
          quotation: project,
          storeName: this.current.vendor ? this.current.vendor.store.name : 'WEB',
        });
        this.print.printPdf(imgPdfBase64.value, !!this.current.vendor);
      }
    }
  }

  async selectCustomerIfNeeded(callback: () => Promise<void>): Promise<void> {
    if (this.customer.userDatas) {
      await callback();
      return;
    }

    return new Promise<void>((resolve, reject) => {
      const onSuccess = async () => {
        this.modalService.close(true);
        try {
          await callback();
          resolve();
        } catch (err) {
          reject(err);
        }
      }

      const onClose = () => {
        if (this.modalService.returnValue !== true) {
          reject(ERROR_TO_IGNORE);
        }
      }

      if (this.isVendorApp) {
        this.modalService.display(MODAL.SEARCH_CUSTOMER, { onCustomerSelect: onSuccess }, false, onClose);
      } else {
        this.modalService.display(MODAL.LOGIN, { loginCallBack: onSuccess }, false, onClose);
      }
    });
  }

  initStepper() {
    this.steps = [
      {
        index: 1,
        title: 'closet-doors.steps.environment',
        gtmTitle: 'Portes de Placard à Mesure | Votre Environnement',
        gtmPageName: 'votre-environnement',
      },
      {
        index: 2,
        title: 'closet-doors.steps.configuration',
        gtmTitle: 'Portes de Placard à Mesure | Portes',
        gtmPageName: 'portes',
      },
      {
        index: 3,
        title: 'closet-doors.steps.range',
        gtmTitle: 'Portes de Placard à Mesure | Gamme',
        gtmPageName: 'gamme',
      },
      {
        index: 4,
        title: 'closet-doors.steps.finishes',
        gtmTitle: 'Portes de Placard à Mesure | Finition',
        gtmPageName: 'finition',
      },
      {
        index: 5,
        title: 'closet-doors.steps.summary',
        gtmTitle: 'Portes de Placard à Mesure | Prix et Résumé',
        gtmPageName: 'prix-et-resume',
      },
    ];
  }

  /**
   * Fonction de mise à jour des étapes du configurateur.
   * L'event correspond à si l'étape est insérée ou suprrimé.
   * Si l'étape est insérée l'event la contient aussi.
   * @param event
   */
  updateStepperItems(event: ConfiguratorUpdateStructureStep) {
    // On vérifie si l'étape de configurateur existe déjà dans le fil d'arriane.
    let stepExist = this.appComponent.steps.find(step => step.injectionToken === event.step.injectionToken);
    // S'il un insère une étape et qu'elle n'existe pas déjà
    if (event.isInsert && (stepExist === null || stepExist === undefined)) {
      this.insertNewStepAndUpdateOthers(event.step);
    } else if (!event.isInsert && stepExist !== null && stepExist !== undefined) {
      this.deleteStepAndUpdateOthers(event.step);
    }
    this.finalStep = this.appComponent.steps.length;
  }

  /**
   * Insère une nouvelle étape au fil d'arianne
   * L'étape est à la position du step et pousse les autres vers les indexs suivants.
   * @param step
   */
  insertNewStepAndUpdateOthers(step: ConfiguratorStructureStep) {
    this.appComponent.steps.splice(step.index - 1, 0, step);
    for (let i = step.index; i < this.appComponent.steps.length; i++) {
      this.appComponent.steps[i].index = this.appComponent.steps[i].index + 1;
    }
  }

  /**
   * Supprime l'étape passé en paramètre et met l'index des suivantes à jour.
   * @param step
   */
  deleteStepAndUpdateOthers(step: ConfiguratorStructureStep) {
    this.appComponent.steps.splice(step.index - 1, 1);
    for (let i = step.index - 1; i < this.appComponent.steps.length; i++) {
      this.appComponent.steps[i].index = this.appComponent.steps[i].index - 1;
    }
  }

  getEmplacementPictureUrl() {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return this.current.closetDoors.emplacement?.picture?.path;
    }
    return this.appComponent.getEmplacementPictureUrl();
  }

  getStepperItems() {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return this.steps;
    }

    return this.appComponent.steps;
  }

  async changeTitle(event: any): Promise<void> {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      this.current.closetDoors.title = event.target.value;
      // La configuration n'est créée qu'à la validation de la première étape.
      // On ne persiste le titre que si elle existe déjà en base.
      if (this.current.closetDoors.id) {
        await this.closetDoorsService.saveTitle(this.current.closetDoors.id, this.current.closetDoors.title);
      }
    } else {
      this.appService.configuration.title = event.target.value;
    }
  }

  getCurrentTitle() {
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      return this.current.closetDoors.title;
    }
    return this.appService.configuration.title;
  }

  /**
   * Fonction de commande
   * @returns boolean
   */
  async order(isPrevisit: boolean): Promise<void> {
    if (this.isOrderButtonDisabled()
      || this.isQuoteAndOrderLoading
      || this.orderingInFlight) return;

    this.isQuoteAndOrderLoading = true;
    this.orderingInFlight = true;

    try {
      await this.selectCustomerIfNeeded(async () => await this.commonOrder(isPrevisit));
    } catch (err) {
      if (err === ERROR_TO_IGNORE) return;
      logError(err);
      throw err;
    } finally {
      this.isQuoteAndOrderLoading = false;
      this.orderingInFlight = false;
    }
  }

  private async commonOrder(isPrevisit: boolean = false): Promise<void> {
    // Récupération des paramètres de l'order : CommonOrder Object
    const commonOrder = await this.getCommonOrder();
    // Tentative génération pdf
    if (commonOrder.checkInstallation && commonOrder.checkOrder) {
      if (isPrevisit) {
        await this.sendOrderPrevisit(commonOrder);
        this.trackEventWithGtm('envoi-commande-previsite');
      } else {
        await this.sendOrder(commonOrder)
        this.trackEventWithGtm('envoi-commande');
      }
    }
  }

  /**
   * Récupération des paramètres de commandes
   * @returns Promise<CommonOrder>
   */
  private async getCommonOrder(): Promise<CommonOrder> {
    // Récupération si ClosetDoorStandard
    if (this.customer.context === CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
      try {
        const data = await this.renderToImgClosetDoors();
        await this.closetDoorsService.saveVisual(this.current.closetDoors.id, { data: data });
        return new CommonOrder(this.closetDoorsService, this.current.closetDoors.id);
      } catch (error) {
        logError(error);
        throw new Error("Une erreur est survenue, l'aperçu n'a pas été généré");
      }
      // Récupération si autre.
    } else {
      if (this.configWithPreview) {
        // Configurateurs avec preview
        try {
          const data = await this.renderToImg();
          this.appService.configuration.thumbnail = { data: data };
        } catch (error) {
          logError(error);
          throw new Error("Une erreur est survenue, l'aperçu n'a pas été généré");
        }
      }

      try {
        await this.appService.saveProject();
        const commonOrder = new CommonOrder(this.appService, this.appService.configuration.id);
        // commonOrder.checkInstallation = await this.appComponent.checkInstallation();
        commonOrder.checkOrder = await this.appComponent.checkOrder();
        return commonOrder;
      } catch (error) {
        logError(error);
        throw new Error('Une erreur est survenue lors de la vérification de la commande');
      }
    }
  }

  private async sendOrder(commonOrder: CommonOrder): Promise<void> {
    try {
      // Création de la commande
      const res = await commonOrder.service.generateXML(commonOrder.id);
      const opportunityId = await this.getOpportunityNumber(res.xml);

      if (commonOrder?.service?.configuration?.["configurationHasChanged"]) {
        commonOrder.service.configuration["configurationHasChanged"] = false;
      }

      if (this.customer.context !== CONFIG_CONTEXT.CLOSET_DOORS_STANDARD) {
        await commonOrder.service.updateOrderAndSaveProject(res.orderId, opportunityId);
        await commonOrder.service.generateNewPdf({
          configContext: this.customer.context,
          configurationId2D: commonOrder.id,
          customerContext: this.translate.instant('popins.products_list.print_title_' + this.customer.context),
          configThumbnail: await this.renderToImg(true, 3000, 2250),
          disclaimer: this.getDisclaimersContext(this.customer.context),
          storeName: this.current.vendor?.store?.name ?? 'WEB'
        });
      }

      alert(`Votre projet est transféré à COM+, ID du dossier : ${opportunityId}`);
    } catch (error) {
      logError(error);
      throw new Error('La génération du devis (XML) a échouée, veuillez contacter un administrateur.');
    }
  }

  private async sendOrderPrevisit(commonOrder: CommonOrder): Promise<void> {
    try {
      const xml = await commonOrder.service.getPrevisit(commonOrder.id);
      const opportunityId = await this.getOpportunityNumber(xml);
      alert(`Votre projet est transféré à COM+, ID du dossier : ${opportunityId}`);
    } catch (error) {
      logError(error);
      throw new Error('La génération du devis de prévisite (XML) a échouée, veuillez contacter un administrateur.');
    }
  }

  private async getOpportunityNumber(xml: string): Promise<number> {
    if (environment.production) {
      try {
        return await this.processQuoteService.sendDotComPlusOrder(xml);
      } catch (err) {
        logError(err);
        throw new Error("La génération du devis (Numéro d'opportunité) à échoué, veuillez contacter un administrateur.");
      }
    } else {
      const blob = new Blob([xml], { type: 'text/xml' });
      const url = globalThis.URL.createObjectURL(blob);

      return new Promise<number>(resolve =>
        setTimeout(() => {
          globalThis.open(url);
          resolve(Math.floor(Math.random() * 1_000_000));
        }, 2000) // Génération d'un entier aléatoire entre 0 et 999999)
      );
    }
  }

  private async updateMainContextConfiguration() {
    if (this.customer.context === this.mainContext) return;
    switch (this.mainContext) {
      case CONFIG_CONTEXT.KITCHEN: {
        await this.kitchenService.updateKitchenWorkbenchConfigurationId(this.appService.configuration.id);
      }
    }
  }

  private async generatePdf() {
    try {
      let originalImgUrl = await this.renderToImg(false);
      // On met volontairement pas d'await car on a pas besoin d'attendre le retour de la génération.
      this.appService.generateNewPdf({
        configContext: this.customer.context,
        configurationId2D: this.appService.configuration.id,
        customerContext: this.translate.instant('popins.products_list.print_title_' + this.customer.context),
        configThumbnail: originalImgUrl,
        disclaimer: this.translate.instant('popins.products_list.aside.disclaimerPdf'),
        quotation: null, // la quotation est récupérée dans le back.
        storeName: this.current.vendor ? this.current.vendor.store.name : 'WEB',
      });

      //Si c'est un workbench, pour générer le PDF on a besoin d'upload l'image dans le storage.
      if (this.customer.context === CONFIG_CONTEXT.WORKBENCH) {
        (this.appService as WorkbenchService).uploadPreview(originalImgUrl);
      }
    } catch (e) {
      logError(e);
    }
  }

  private async generateThumbnail() {
    let resizedImgUrl = await this.renderToImg();
    this.appService.configuration.thumbnail = { data: resizedImgUrl };
  }

  /**
   * Quand on sort du configurateur en mode tunnel
   */
  async nextTunnelStep(): Promise<void> {
    try {
      await this.generateThumbnail();
      await this.appService.saveProject();
      this.tunnelDataStore.updateConfigurationData(
        this.customer.context as CONFIG_CONTEXT,
        this.appService.configuration
      );

      await this.updateMainContextConfiguration();
      await this.generatePdf();

      this.tunnelNavigationStore.nextStep();

      this.trackEventWithGtm('enregistrer-projet');
      this.alertService.success(this.translate.instant('generic.saved-success'), this.alertOptions);
    } catch (error) {
      this.alertService.error(this.translate.instant('generic.saved-failed'), this.alertOptions);
      logError(error);
    }
  }

  /**
   * Envoi les événements de VPV du GTM aux changements de pages via le bouton ou le fil d'arianne
   */
  private trackPageWithGtm() {
    const currentStep =
      this.customer.context !== CONFIG_CONTEXT.CLOSET_DOORS_STANDARD
        ? this.appComponent.steps.find(s => s.index === this.step)
        : this.steps.find(s => s.index === this.step);

    this.gtm.trackPage(
      currentStep.gtmPageName ? `${this.router.url}/${currentStep.gtmPageName}` : this.router.url,
      currentStep.gtmTitle,
      {
        stepNumber: this.step,
        totalSteps: this.finalStep,
      }
    );

    this.trackBasketCtaEligibility();
  }

  /**
   * CA9 : à l'affichage du CTA principal (étape finale, côté client), envoie un événement analytics
   * indiquant si le projet est éligible ou non à l'ajout au panier.
   */
  private trackBasketCtaEligibility(): void {
    if (this.isVendorApp || this.step !== this.finalStep) {
      return;
    }

    this.gtm.trackEvent('affichage-cta-panier', {
      configurator: this.customer.context,
      eligible: this.featureSettingsService.isBasketSendEnabled(this.customer.context),
    });
  }

  /**
   * Envoi les événements tracké.
   * @param eventName Nom de l'événement
   * @param customDatas Données
   */
  private trackEventWithGtm(eventName: string, extra?: Record<string, unknown>) {
    this.gtm.trackEvent(eventName, {
      projectType: this.currentProjectType,
      projectId: this.currentBasketConfigurationId,
      ...extra,
    });
  }

  private getDisclaimersContext(context: string): string {
    return context === CONFIG_CONTEXT.CLOSET_DOORS_CUSTOM
      ? this.translate.instant('popins.products_list.aside.closetDoorsCustomDisclaimer')
      : this.translate.instant('popins.products_list.aside.disclaimerPdf');
  }

  onSplashbackChanged(value: boolean) {
    this.isOnlySplashback = value;
  }

  onDisableNextFromSplashback(disable: boolean) {
    this.isSplashbackForceDisabled = disable;
  }
}
